import camelcaseKeys from "camelcase-keys";
import Cookie from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { storageFactory } from "storage-factory";
import { logException } from "@wss/error-tracking";
import { toError } from "@wss/error-tracking/utils";
import { Throwable } from "@wss/error-tracking/throwable";
import { usePageData } from "@wss/react";
import { useComponentStore } from "@wss/store";
import { useCookies } from "react-cookie";
import NotificationCenter from "~/common/Notification/NotificationCenter";
import { getTotalRatings } from "~/common/Reviews/CustomerReviewHelper";
import { User } from "~/common/Store/models/User";
import { defaultUserObject } from "~/common/Store/rootReducer";
import useOnClickOutside from "~/common/hooks/useOnClickOutside";
import useIsMounted from "~/common/useIsMounted";
import getCarouselPlacement from "./Carousel/getCarouselPlacement";
import EverlivingProductDetailPricingBox from "./EverlivingProductDetailPricingBox";
import FindRelatedProducts from "./FindRelatedProducts";
import InternalNotesButton from "./InternalNotes/InternalNotesButton";
import InternalNotesModal from "./InternalNotes/InternalNotesModal";
import getMinBuyForQuantityDropdown from "./Pricing/AddToCart/getMinBuyForQuantityDropdown";
import ProductCutDownModal from "./ProductCutDown/ProductCutDownModal";
import ProductDetailHeading from "./ProductDetailHeading";
import {
  ProductDetailPageResult,
  ProductDetailTemplateGroup,
  Templates,
} from "./ProductDetailPageResult";
import ProductDetailPricingBox from "./ProductDetailPricingBox";
import RecommendationCarousel from "./RecommendationCarousel";
import RemoveOpengraphMetadataFromDocument from "./RemoveOpengraphMetadataFromDocument";
import RequestToQuote from "./RequestToQuote";
import Schemas from "./Schemas/Schemas";
import SeeMoreProductsCarousel from "./SeeMoreProductsCarousel";
import StickyHeader from "./StickyHeader";
import StickyHeaderNew, {
  Details,
  Pricing,
  Navigation,
} from "./components/StickyHeader";
import TallCarouselNoCartButton from "./TallCarouselNoCartButton";
import OutletHeadingLabel from "./Templates/OutletTemplate/OutletHeadingLabel";
import OutletPricingBox from "./Templates/OutletTemplate/OutletPricingBox";
import YouMayAlsoNeedCarousel from "./YouMayAlsoNeedCarousel";
import { ProductDetailsGlobalContext } from "./useGlobalContext";
import { VirtualGroupingProvider } from "./useVirtualGroupingContext";
import RelatedItemsCarousel from "./RelatedItems/RelatedItemsCarousel";
import { homepage } from "~/common/Constants/Constants";
import { productPageView } from "~/analytics/PageView/pageViewHelper";
import {
  ProductDetailsContext,
  ProductDetailsStore,
} from "./ProductDetailsStore";
import { useFeatureFlag } from "~/common/contexts/FeatureFlagProvider";
import SimilarProductsContainer from "./SimilarProducts/Container";
import updateVariationIds from "~/Gtm/updateVariationIds";
import LoginModal from "./Pricing/LoginModal";
import { VariationIds } from "~/common/globals";
import ReviewsV2 from "~/common/Reviews";
import { SpotlightBanner } from "./SpotlightBanner";
import { ProductGallerySection } from "./ProductGallerySection";
import { MainColumnSection } from "./MainColumnSection";
import { SideColumnBottomSection } from "./SideColumnBottomSection";
import { SideColumnTopSection } from "./SideColumnTopSection";
import withProvider from "~/common/hocs/withProvider";
import { withUserAndChat } from "~/common/Store/connectUserAndChat";
import evaluateDisplayPrice, {
  presets,
} from "~/features/pricing/pricingResolver";
import withFeatureFlags from "~/common/hocs/withFeatureFlags";
import { cls } from "@wss/cls";

const getStartBuy = (template?: ProductDetailPageResult) =>
  (template && Math.max(template.minimumBuy, template.mustBuy)) || 1;
const quantitySelectExpression: (item?: ProductDetailPageResult) => boolean = (
  item,
) => !!(item && (item.minimumBuy > 1 || item.mustBuy > 1));

export function useProductDetailPageData(): ProductDetailTemplateGroup {
  return usePageData("ProductDetails");
}

const ProductDetails = ({ user }: { user: User }) => {
  const { evaluateFlag } = useFeatureFlag();
  const {
    productDetailGroupingSelect,
    productTemplates,
    isGoogleView,
    creditKeyConfig,
    virtualGroupOptionsMap = [],
    groupMembers = [],
    isUtmMediumCPC = false,
  } = useProductDetailPageData();
  const localStorage = storageFactory(() => window.localStorage);
  const store = useComponentStore<ProductDetailsStore>(() => ({
    isGoogleView: isGoogleView || false,
  }));
  const [activeItemNumberIndex, setActiveItemNumberIndex] = useState(0);
  const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);
  const [showCpcDisplayMinimalVersion, setShowCpcDisplayMinimalVersion] =
    useState(false);
  const [
    isZipcodeEligibleForInstallationService,
    setIsZipcodeEligibleForInstallationService,
  ] = useState<boolean>(false);

  const [showSpotlightBanner, setShowSpotlightBanner] = useState(true);

  const mounted = useIsMounted();
  const activeTemplate = productTemplates[activeItemNumberIndex];
  if (activeTemplate) {
    activeTemplate.groupingAttributes.groupMembers = groupMembers;
  }

  const [cookies] = useCookies(["pageVersion", "pageVariation"]);

  const pricingModuleEnabled = evaluateFlag("pdp-pricing-rewrite");

  useEffect(() => {
    if (cookies.pageVariation && cookies.pageVersion) {
      setShowCpcDisplayMinimalVersion(cookies.pageVersion === "minimal");

      // GTM tracking
      updateVariationIds([
        {
          featureName: VariationIds.Cpc_AB_Test,
          featureOption: cookies.pageVariation === "B" ? "1" : "0", // 50/50 A/B test
        },
      ]);
    }
  }, [cookies]);

  const minimalPageVersion =
    activeTemplate?.isCpcMinimalPageVersion && showCpcDisplayMinimalVersion;

  const setActiveBreadcrumbDisplay = (heading: string) => {
    if (heading === "") {
      return;
    }
    const breadcrumb = document.getElementById("breadCrumbCurrent");
    if (breadcrumb) {
      breadcrumb.innerText = heading;
    }
  };

  useEffect(() => {
    if (creditKeyConfig?.publicKeyId && !Cookie.get("CreditKeyPublicKey")) {
      Cookie.set("CreditKeyPublicKey", creditKeyConfig.publicKeyId);
    }

    if (
      creditKeyConfig?.minimumDisplayPrice &&
      !Cookie.get("CreditKeyPdpMinimumDisplayPrice")
    ) {
      Cookie.set(
        "CreditKeyPdpMinimumDisplayPrice",
        creditKeyConfig.minimumDisplayPrice.toString(),
      );
    }
  });

  useEffect(() => {
    async function importCarouselFunctionality() {
      if (document.querySelector("[data-component=carousel]")) {
        const { default: initCategoryCarousels } = await import(
          "~/common/initCategoryCarousels"
        );
        initCategoryCarousels();
      }
    }
    importCarouselFunctionality();
  }, [activeItemNumberIndex]);

  const showStickyHeaderKey = "showStickyHeaderFlag";
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [stickyHeaderCollapsed, setStickyHeaderCollapsed] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);

  const [expandedDescriptionOffABTest, setExpandedDescriptionOffABTest] =
    useState(false);

  useEffect(() => {
    if (mounted) {
      setStickyHeaderCollapsed(
        localStorage.getItem(showStickyHeaderKey) === "true",
      );
    }
  }, [mounted, localStorage]);

  const handleOutletAddToCart = () => {
    setOutletItemInCart(true);
    setAddToCartSuccess(true);
  };

  const handleStickyHeaderCollapse = () => {
    const collapsed = !stickyHeaderCollapsed;

    localStorage.setItem(showStickyHeaderKey, collapsed.toString());
    setStickyHeaderCollapsed(collapsed);
  };

  useEffect(
    function initIntersectionObserverEffect() {
      function handleIntersections(entries: IntersectionObserverEntry[]) {
        setShowStickyHeader(!entries[0]?.isIntersecting);
      }
      const observer = new IntersectionObserver(handleIntersections);
      const element = document.querySelector("#subject");
      if (element) observer.observe(element);

      return function cleanupIntersectionObserver() {
        observer.disconnect();
      };
    },
    [activeTemplate],
  );

  const newProduct = !!activeTemplate?.isNewProduct;

  const [hasQuantitySelect, setHasQuantitySelect] = useState(
    quantitySelectExpression(activeTemplate),
  );

  const startBuy = getStartBuy(activeTemplate);
  const [quantity, setQuantity] = useState(
    hasQuantitySelect || startBuy > 1 ? "0" : startBuy.toString(),
  );

  const handleQuantityChanged = (value: string) => {
    if (value === "other") {
      setHasQuantitySelect(false);
      setQuantity(
        getMinBuyForQuantityDropdown({
          minBuy: activeTemplate?.minimumBuy ?? 1,
          mustBuy: activeTemplate?.mustBuy ?? 1,
        }).toString(),
      );
      return;
    }
    setQuantity(value);
  };

  const handleVirtualGroupingSelectChange = (itemNumber: string) => {
    if (itemNumber === "") {
      setActiveItemNumberIndex(0);
      return productTemplates[0]?.link ?? "";
    }
    const activeIndex = productTemplates.findIndex(
      (p) => p.itemNumber === itemNumber,
    );
    setActiveItemNumberIndex(activeIndex);
    setActiveBreadcrumbDisplay(productTemplates[activeIndex]?.header ?? "");
    setHasQuantitySelect(
      quantitySelectExpression(productTemplates[activeIndex]),
    );
    setQuantity(getStartBuy(productTemplates[activeIndex]).toString());
    return productTemplates[activeIndex]?.link ?? "";
  };

  const displayPricingBoxForTemplate = (
    activeTemplate: ProductDetailPageResult,
    user: User,
  ) => {
    const {
      itemNumber,
      outletAttributes,
      template,
      showEverlivingProductPage,
      allowOutOfStockPurchase,
      isRequestAQuoteEnabled,
    } = activeTemplate;

    if (template === Templates.OutletTemplate) {
      return (
        <OutletPricingBox
          product={activeTemplate}
          outletAttributes={outletAttributes}
          itemInCart={outletItemInCart}
          onAddToCart={handleOutletAddToCart}
          user={user}
          mounted={mounted}
        />
      );
    }
    if (showEverlivingProductPage) {
      return <EverlivingProductDetailPricingBox product={activeTemplate} />;
    }
    if (isRequestAQuoteEnabled) {
      return (
        <RequestToQuote
          newProduct={newProduct}
          requestAQuoteUrl={activeTemplate.requestAQuoteUrl}
        />
      );
    }
    return (
      <ProductDetailPricingBox
        key={`pricingBox-${itemNumber}`}
        mounted={mounted}
        product={activeTemplate}
        quantity={quantity}
        hasQuantitySelect={hasQuantitySelect}
        onQuantityChanged={handleQuantityChanged}
        user={defaultUserObject}
        handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange}
        setAddToCartSuccess={setAddToCartSuccess}
        allowOutOfStockPurchase={allowOutOfStockPurchase}
        minimalPageVersion={minimalPageVersion}
      />
    );
  };

  const [outletItemInCart, setOutletItemInCart] = useState(false);

  useEffect(() => {
    if (!activeTemplate?.outletAttributes?.outletProductId) {
      return;
    }

    type ShoppingCartItem = {
      outletItemId: number;
      isOutletItem: number;
      itemNumber: string;
    };

    async function getShoppingCartItems(): Promise<Throwable> {
      const endpointUrl = "/api/shoppingcart/items";

      try {
        const response = await fetch(endpointUrl);
        const result = await response.json<ShoppingCartItem[]>();
        const cartItems = camelcaseKeys(result);
        if (
          cartItems.find(
            (c) =>
              c.outletItemId ===
              activeTemplate?.outletAttributes?.outletProductId,
          )
        ) {
          setOutletItemInCart(true);
        }

        return { isError: false };
      } catch (e) {
        const error = new Error("Error in getShoppingCartItems", {
          cause: toError(e),
        });

        await logException(error);

        setOutletItemInCart(false);

        return { error, isError: true };
      }
    }

    getShoppingCartItems();
  }, [activeTemplate?.outletAttributes?.outletProductId]);

  useEffect(() => {
    if (activeTemplate && activeTemplate.itemNumber) {
      productPageView(activeTemplate.itemNumber);
    }
  }, [activeTemplate]);

  const internalNotesRef = useRef(null);
  useOnClickOutside(internalNotesRef, () => setShowInternalNotesModal(false));

  if (activeTemplate && productTemplates[0]) {
    const {
      actualVendorItemNumber,
      breadCrumbs,
      originalId,
      productImages,
      productImageDisclaimer,
      primaryBrand,
      defaultCategory,
      itemNumber,
      title,
      videos,
      modelFiles,
      otherProductsFromThisLine,
      youMayAlsoNeedCarouselItems,
      otherProductsCarouselPromotion,
      price,
      productCutDown,
      link,
      variationMembership,
      header,
      template,
      productAds,
      isVirtualPart,
      seeMoreProductsCarouselItems,
      showSeeMoreProductsCarousel,
      showEverlivingProductPage,
      showVendorItemNumber,
      relatedSearchTerms,
      youMayAlsoNeedUrl,
      description,
    } = activeTemplate;

    const { customerReviews } = productTemplates[0];

    const carouselPlacement = getCarouselPlacement(activeTemplate);

    const isVirtualGroupMaster =
      activeTemplate.groupingAttributes.isGroupMaster &&
      activeTemplate.groupingAttributes.isVirtual;

    const clonedItem = productTemplates?.find(
      (x) => x.itemNumber === originalId,
    );

    const socialLink = (isVirtualGroupMaster && clonedItem?.link) || link;
    const socialTitle = (isVirtualGroupMaster && clonedItem?.header) || header;
    const isExpandedDescriptionTemplate =
      template === Templates.ExpandedDescriptionTemplate &&
      !expandedDescriptionOffABTest;
    const isOutletTemplate = template === Templates.OutletTemplate;

    const shouldShowRelatedItemsCarousel =
      !minimalPageVersion &&
      !showEverlivingProductPage &&
      defaultCategory?.id &&
      defaultCategory?.name &&
      breadCrumbs?.length > 0;

    const resolvedPricing = evaluateDisplayPrice(
      {
        user,
        p1: activeTemplate.price.price,
        p4: activeTemplate.price.minimumAdvertisedPriceProperties.price,
        p5: activeTemplate.price.minimumAdvertisedPriceOverride?.price,
        originalPrice: activeTemplate.price.originalPrice,
        salePrice: activeTemplate.price.salePrice,
        unitOfMeasure: activeTemplate.unitOfMeasure,
        mapStyle:
          activeTemplate.price.minimumAdvertisedPriceProperties.type.trim(),
        overrides: {
          loggedIn:
            activeTemplate.showBelowMinimumAdvertisedPriceForLoggedInCustomers,
          platPlus:
            activeTemplate.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
        },
        minimumQuantity: activeTemplate.minimumBuy,
        mustBuy: activeTemplate.mustBuy,
        divideByQuantity: activeTemplate.divideQuantity,
        divideByUnit: activeTemplate.divideByUnit,
        isCustomizable: activeTemplate.isCustomizable,
        requiresCustomization: activeTemplate.customizationRequired,
        minimumCustomizationPrice: activeTemplate.minimumCustomizationPrice,
        isGoogleView: !!isGoogleView,
        quantityDiscounts: activeTemplate.price.quantityDiscounts?.map(
          (qd) => ({
            price: qd.price,
            lot: qd.isLotDiscount,
            divideByUnit: activeTemplate.divideByUnit,
            divideByQuantity: activeTemplate.divideQuantity,
            range: {
              start: qd.startQuantity,
              end: qd.endQuantity,
            },
          }),
        ),
        // qualifier: () => true, todo
      },
      presets.comprehensiveEvaluation,
    );

    // only applies to new sticky header
    const showStickyHeaderPrice = !!(
      activeTemplate.isInStock ||
      !activeTemplate.isReplacementEligible ||
      (activeTemplate.customerExclusivity.disallowNonMemberToPurchase &&
        !activeTemplate.customerExclusivity.allowPlatinumToPurchase &&
        !activeTemplate.customerExclusivity.allowWebstaurantPlusToPurchase) ||
      activeTemplate.groupingAttributes.isGroupMaster ||
      (resolvedPricing.metadata?.mapStyle === "G" &&
        resolvedPricing.metadata?.googleView) ||
      (resolvedPricing.metadata?.mapStyle &&
        resolvedPricing.metadata?.mapStyle !== "G" &&
        !resolvedPricing.metadata?.originalPrice &&
        !resolvedPricing.metadata?.belowMapQualified)
    );

    return (
      <ProductDetailsContext.Provider value={store}>
        <RemoveOpengraphMetadataFromDocument
          user={defaultUserObject}
          mounted={mounted}
        />
        <ProductDetailsGlobalContext.Provider
          value={{
            isZipcodeEligibleForInstallationService,
            setIsZipcodeEligibleForInstallationService,
          }}
        >
          <VirtualGroupingProvider
            virtualGroupOptionsMap={virtualGroupOptionsMap}
          >
            {pricingModuleEnabled && !showEverlivingProductPage && (
              <StickyHeaderNew
                className={cls(
                  (showStickyHeader && "hidden top-0 md:block") || "hidden",
                )}
              >
                <Details
                  thumbnailSrc={
                    productImages.find((i) => i.isPrimaryDetailImage)
                      ?.thumbnail ||
                    productImages[0]?.thumbnail ||
                    ""
                  }
                  description={activeTemplate.description}
                  rating={activeTemplate.customerReviews.oneToFiveStarRating}
                  itemNumber={activeTemplate.itemNumber}
                  handleNavigateToReviews={() => {
                    if (minimalPageVersion) {
                      window.location.href = `/product-reviews/${activeTemplate.itemNumber}.html`;
                      return;
                    }

                    const reviews = document?.getElementById("reviews");
                    reviews?.scrollIntoView();
                    reviews?.focus();
                  }}
                />
                <Pricing
                  minBuy={activeTemplate.minimumBuy ?? 1}
                  mustBuy={activeTemplate.mustBuy ?? 1}
                  showPrice={showStickyHeaderPrice}
                  showQuantitySelect={hasQuantitySelect}
                  resolvedPricing={resolvedPricing}
                  atcSuccess={addToCartSuccess}
                  setAtcSuccess={setAddToCartSuccess}
                  requestAQuoteEnabled={false}
                  requiresAccessories={activeTemplate.hasRequiredAccessories}
                  quantity={quantity}
                  onQuantityChange={handleQuantityChanged}
                  onAddToCart={() =>
                    document
                      ?.querySelector<HTMLButtonElement>("#buyButton")
                      ?.click()
                  }
                  customizationUrl={activeTemplate.customizationUrl}
                  user={user}
                />
                <Navigation
                  reviewCount={
                    customerReviews.productReviewDetails.filter(
                      (r) => r.oneToFiveStarRating > 0,
                    ).length
                  }
                  replacementEligible={activeTemplate.isReplacementEligible}
                  minimal={minimalPageVersion ?? false}
                />
              </StickyHeaderNew>
            )}
            {!pricingModuleEnabled && !showEverlivingProductPage && (
              <StickyHeader
                product={activeTemplate}
                quantity={quantity}
                hasQuantitySelect={hasQuantitySelect}
                onQuantityChanged={handleQuantityChanged}
                show={showStickyHeader}
                collapsed={stickyHeaderCollapsed}
                onClose={handleStickyHeaderCollapse}
                setAddToCartSuccess={setAddToCartSuccess}
                addToCartSuccess={addToCartSuccess}
                isGoogleView={isGoogleView}
                alreadyInCart={outletItemInCart}
                mounted={mounted}
                customerReviews={customerReviews}
                minimalPageVersion={minimalPageVersion}
              />
            )}
            <div
              className={`container pdp-v3 ${
                showEverlivingProductPage ? `container--everliving` : ``
              }`}
              data-component="storefront-product-detail"
            >
              <InternalNotesButton
                showInternalNotesModal={showInternalNotesModal}
                toggleInternalNotesModal={() =>
                  setShowInternalNotesModal(!showInternalNotesModal)
                }
                user={defaultUserObject}
                mounted={mounted}
                itemNumber={originalId}
              />
              {showInternalNotesModal && (
                <InternalNotesModal
                  outletItemNumber={activeTemplate.itemNumber}
                  toggleInternalNotesModal={() =>
                    setShowInternalNotesModal(!showInternalNotesModal)
                  }
                  isVirtual={activeTemplate.groupingAttributes.isVirtual}
                  itemNumber={
                    isVirtualGroupMaster || isOutletTemplate || isVirtualPart
                      ? originalId
                      : itemNumber
                  }
                  groupItemNumber={
                    activeTemplate.groupingAttributes.virtualGroupItemNumber
                  }
                  groupingItemNumbers={groupMembers.map((item) =>
                    item.itemNumber.toUpperCase(),
                  )}
                  internalNotesRef={internalNotesRef}
                />
              )}
              {showSpotlightBanner &&
                !showEverlivingProductPage &&
                !isOutletTemplate &&
                !isUtmMediumCPC &&
                !showSeeMoreProductsCarousel && (
                  <SpotlightBanner
                    activeTemplate={activeTemplate}
                    setShowSpotlightBanner={setShowSpotlightBanner}
                  />
                )}
              <ProductDetailHeading
                itemNumber={originalId}
                descriptionHtml={header}
                rating={customerReviews.overallRating}
                numberOfReviews={
                  getTotalRatings(customerReviews.productReviewDetails).length
                }
                showVendorItemNumber={showVendorItemNumber}
                actualVendorItemNumber={actualVendorItemNumber}
                isEverLiving={showEverlivingProductPage}
                isOutlet={isOutletTemplate}
                numberOfAnswers={
                  activeTemplate.questionsAndAnswers?.length ?? 0
                }
                isGroupMaster={activeTemplate.groupingAttributes.isGroupMaster}
                minimalPageVersion={minimalPageVersion}
              />
              {showSeeMoreProductsCarousel && (
                <SeeMoreProductsCarousel
                  seeMoreProductsCarouselItems={seeMoreProductsCarouselItems}
                  category={defaultCategory}
                  isEverliving={showEverlivingProductPage}
                />
              )}
              {isOutletTemplate && <OutletHeadingLabel />}
              <div
                id="page"
                className={`product-page${
                  isExpandedDescriptionTemplate ? " expanded-detail" : ""
                }`}
              >
                {!showEverlivingProductPage &&
                  !isOutletTemplate &&
                  productCutDown &&
                  productAds && (
                    <ProductCutDownModal
                      productCutDown={productCutDown}
                      productName={title}
                      productItemNumber={originalId.toLocaleUpperCase()}
                      productPrice={price.price}
                      isVirtual={activeTemplate.groupingAttributes.isVirtual}
                      isVirtualGroupMaster={isVirtualGroupMaster}
                      productDetailGroupingSelect={productDetailGroupingSelect}
                      handleVirtualGroupingSelectChange={
                        handleVirtualGroupingSelectChange
                      }
                      selectedItemNumber={
                        activeTemplate.groupingAttributes.isGroupMaster
                          ? ""
                          : itemNumber
                      }
                      hasQuantityDiscounts={price.hasQuantityDiscounts}
                      quantityDiscountPrice={
                        price.hasQuantityDiscounts
                          ? price.quantityDiscounts[0]?.price
                          : 0
                      }
                      quantityDiscountQty={
                        price.hasQuantityDiscounts
                          ? price.quantityDiscounts[0]?.startQuantity
                          : 0
                      }
                      mounted={mounted}
                    />
                  )}
                <ProductGallerySection
                  productImages={productImages}
                  isOutletTemplate={isOutletTemplate}
                  videos={videos}
                  modelFiles={modelFiles}
                  productImageDisclaimer={productImageDisclaimer}
                  activeItemNumberIndex={activeItemNumberIndex}
                  title={title}
                  minimalPageVersion={minimalPageVersion}
                  socialLink={socialLink}
                  socialTitle={socialTitle}
                  titleSection={carouselPlacement.showYMANCarousel}
                  youMayAlsoNeedUrl={youMayAlsoNeedUrl}
                  isVirtualGroupMaster={isVirtualGroupMaster}
                  showEverlivingProductPage={showEverlivingProductPage}
                />
                <div className="-order-3 lt:order-none lt:float-right lt:clear-right">
                  {price &&
                    displayPricingBoxForTemplate(
                      activeTemplate,
                      defaultUserObject,
                    )}
                  <SideColumnTopSection
                    activeTemplate={activeTemplate}
                    groupMembers={groupMembers}
                    mounted={mounted}
                    minimalPageVersion={minimalPageVersion}
                  />
                </div>
                {carouselPlacement.showYMANCarousel && (
                  <YouMayAlsoNeedCarousel
                    key={`ymanCarousel-${itemNumber}`}
                    carouselItems={youMayAlsoNeedCarouselItems}
                    youMayAlsoNeedUrl={youMayAlsoNeedUrl}
                  />
                )}
                {carouselPlacement.showRecAiCarousel && (
                  <RecommendationCarousel
                    key={`recommendationCarousel-${itemNumber}`}
                    product={activeTemplate}
                    mounted={mounted}
                  />
                )}
                <MainColumnSection
                  activeTemplate={activeTemplate}
                  carouselPlacement={carouselPlacement}
                  isExpandedDescriptionTemplate={isExpandedDescriptionTemplate}
                  mounted={mounted}
                  minimalPageVersion={minimalPageVersion}
                  setExpandedDescriptionOffABTest={
                    setExpandedDescriptionOffABTest
                  }
                />
                <SideColumnBottomSection
                  activeTemplate={activeTemplate}
                  isExpandedDescriptionTemplate={isExpandedDescriptionTemplate}
                />
                {carouselPlacement.showOPFTLCarouselBelowDescription && (
                  <TallCarouselNoCartButton
                    key={`otherProductsTallCarousel-${itemNumber}`}
                    id="other-products-from-this-line-carousel"
                    carouselItems={otherProductsFromThisLine}
                    carouselPromotionMember={otherProductsCarouselPromotion}
                    heading="Other Products from this Line"
                    dataComponentName="other-products-from-this-line-carousel"
                    gtmCarouselName="Other Products from this Line"
                    supportsRequestQuoteProgram
                  />
                )}
                {shouldShowRelatedItemsCarousel && (
                  <RelatedItemsCarousel
                    key={`relatedItemsTallCarousel-${itemNumber}`}
                    itemNumber={itemNumber}
                    itemTitle={title}
                    categoryId={defaultCategory.id.toString()}
                    categoryTitle={defaultCategory.name}
                    breadcrumbs={breadCrumbs}
                  />
                )}
                {!minimalPageVersion && (
                  <ReviewsV2
                    brand={primaryBrand.name}
                    itemNumber={itemNumber}
                    reviews={customerReviews}
                    mounted={mounted}
                    isVirtualGroupMaster={isVirtualGroupMaster}
                    vgMasterOriginalItemNumber={activeTemplate.originalId}
                    productDetailGroupingSelect={productDetailGroupingSelect}
                    handleVirtualGroupingSelectChange={
                      handleVirtualGroupingSelectChange
                    }
                    isEverliving={showEverlivingProductPage}
                    image={
                      productImages.find((image) => image.isPrimaryDetailImage)
                        ?.thumbnail ??
                      "/images/products/new/imagecomingsoon_tn.jpg"
                    }
                    description={description}
                    productLink={activeTemplate?.link ?? homepage}
                    stickyHeaderCollapsed={
                      showStickyHeader && stickyHeaderCollapsed
                    }
                  />
                )}
                {!isOutletTemplate &&
                  !showEverlivingProductPage &&
                  relatedSearchTerms?.length > 0 && (
                    <FindRelatedProducts
                      relatedSearchTerms={relatedSearchTerms}
                    />
                  )}
              </div>
              {minimalPageVersion && (
                <SimilarProductsContainer
                  key="similar-products"
                  product={activeTemplate}
                  mounted={mounted}
                  user={defaultUserObject}
                />
              )}
              <NotificationCenter />
            </div>
            <Schemas
              activeProduct={activeTemplate}
              user={defaultUserObject}
              mounted={mounted}
              variationMembership={variationMembership}
            />
            <LoginModal />
          </VirtualGroupingProvider>
        </ProductDetailsGlobalContext.Provider>
      </ProductDetailsContext.Provider>
    );
  }
  return null;
};

export default withFeatureFlags(withProvider(withUserAndChat(ProductDetails)));
